import { useCallback, useEffect, useMemo, useState } from 'react';
import BordOneToneIcon from 'components/NudosComponents/BordOneToneIcon/BordOneToneIcon';
import InvoiceSectionOfBillingTab from 'components/InvoiceSectionOfBillingTab/InvoiceSectionOfBillingTab';
import { getStorageBillingInvoiceById } from 'services/billing/storageBillingInvoices.service';
import { nudosGray500 } from 'styles/styles.constants';
import { IStorageBillingInvoices } from 'types/billing.types';
import { TAX_MANAGEMENT_OPTIONS } from 'types/global';
import { ICountryDetail } from 'types/bussines';
import { displayErrorNotification } from 'utils/displayNudosStandardNotifications';
import { DateTime } from 'luxon';
import BillingInfo from './components/BillingInfo/BillingInfo';
import StorageInfo from './components/StorageInfo/StorageInfo';
import PaymentInfo from './components/PaymentInfo/PaymentInfo';
import useInvoicesState from 'state/useInvoicesState';
import useLockScroll from 'hooks/useLockScroll';
import './BillingDetailModalContent.scss';

interface IBillingDetailModalContentProps {
  onClose: () => void;
  billingInvoiceId: number;
  countryList: ICountryDetail[];
}

const BillingDetailModalContent = ({ billingInvoiceId, countryList, onClose }: IBillingDetailModalContentProps) => {
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [billingInvoice, setBillingInvoice] = useState<IStorageBillingInvoices>();
  const { setStorageInvoiceId } = useInvoicesState();
  useLockScroll(true);

  const organizationInfo = billingInvoice?.organizationInfo;
  const billingMethod = billingInvoice?.billingMethod;

  const billingCountry = useMemo(() => {
    return countryList.find(country => country.code === billingMethod?.billingCountry);
  }, [countryList, billingMethod?.billingCountry]);

  const taxType = useMemo(
    () => billingMethod?.taxManagementName as TAX_MANAGEMENT_OPTIONS,
    [billingMethod?.taxManagementName]
  );

  const invoicingMethodsWithIVA = useMemo(() => {
    if (!taxType) return false;
    return [
      TAX_MANAGEMENT_OPTIONS.BREAK_DOWN_ECOMMERCE_TAX,
      TAX_MANAGEMENT_OPTIONS.COMPLETE_TAXES_AFTER_ORDER_CREATION
    ].includes(taxType);
  }, [taxType]);

  const formatMonth = useCallback((monthKey?: string): string => {
    if (!monthKey) return '';
    const [year, month] = monthKey.split('-');
    if (!year || !month) return '';
    const date = DateTime.fromObject({ year: Number(year), month: Number(month) });
    const formatted = date.setLocale('es').toFormat('LLLL yyyy');
    return formatted.charAt(0).toUpperCase() + formatted.slice(1);
  }, []);

  const subtitle = useMemo(() => {
    if (!organizationInfo?.organizationName) return '';
    return `${organizationInfo.organizationName} - ${formatMonth(billingInvoice?.month)}`;
  }, [organizationInfo?.organizationName, billingInvoice?.month, formatMonth]);

  const getBillingInvoice = useCallback(async () => {
    if (!billingInvoiceId) return;
    setIsLoading(true);
    try {
      const invoice = await getStorageBillingInvoiceById(billingInvoiceId);
      setBillingInvoice(invoice);
    } catch {
      displayErrorNotification();
      onClose();
    } finally {
      setIsLoading(false);
    }
  }, [billingInvoiceId, onClose]);

  const storageInvoiceData = () => {
    setStorageInvoiceId(billingInvoiceId);
  };

  useEffect(() => {
    getBillingInvoice();
  }, [getBillingInvoice]);

  useEffect(() => {
    storageInvoiceData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [billingInvoice]);

  return (
    <div className="billingDetailModalContent">
      <div className="header">
        <p className="title">Cargar factura</p>
        <div className="exitContentButton" onClick={onClose}>
          <BordOneToneIcon variant="x" customWidth="16px" stroke={nudosGray500} />
        </div>
      </div>
      <div className="content">
        <div className="contentHeader">
          {isLoading ? (
            <>
              <div className="contentHeaderSkeleton animationLoader" />
              <div className="contentHeaderSubtitleSkeleton animationLoader" />
            </>
          ) : (
            <>
              <div className="contentHeaderStorageInvoice">
                <span className="contentHeaderTitle">Factura de almacenamiento</span>
                <span className="contentHeaderIdValue">ID: {billingInvoiceId}</span>
              </div>
              <span className="contentHeaderSubtitle">{subtitle}</span>
            </>
          )}
        </div>
        <StorageInfo billingInvoice={billingInvoice} isLoading={isLoading} />
        <>
          <PaymentInfo billingInvoice={billingInvoice} isLoading={isLoading} handleUpdateProof={getBillingInvoice} />
          <div className="divider" />
        </>
        <BillingInfo billingInvoice={billingInvoice} isLoading={isLoading} />

        {isLoading ? (
          <div className="invoiceSectionOfBillingTabSkeleton animationLoader" />
        ) : (
          <InvoiceSectionOfBillingTab
            storageInvoiceId={billingInvoiceId}
            companyName={billingMethod?.beneficiary || ''}
            emissionCountry={billingCountry?.name || ''}
            emissionCountryFlag={billingCountry?.flag || ''}
            billingCountry={billingCountry}
            hasIVA={invoicingMethodsWithIVA}
            variant="storage"
            needInvoice={false}
            billingProvider={billingMethod?.billingProvider}
          />
        )}
      </div>
    </div>
  );
};

export default BillingDetailModalContent;
